import { Add, Error, FilterList } from '@mui/icons-material';
import {
  Button,
  Chip,
  Fab,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { pluralize } from '@prismamedia/one-components';
import type { FC } from 'react';
import { useContext, useMemo, useState } from 'react';
import FlipMove from 'react-flip-move';
import { useParams } from 'react-router-dom';
import { Skeletons } from '../../../../components/Skeletons';
import { paths } from '../../../../routing/Router/paths';
import { auth } from '../../../../utils/auth';
import { useQueryParams } from '../../../../utils/useUriHelpers';
import { CategoriesContext } from '../../categories.context';
import { LARGE_CARD_ITEM_HEIGHT } from '../../components/Card/styles';
import { DataTestIdListPage } from '../../tests/constants';
import type { Category } from '../../types';
import { navigateTo } from '../../utils';
import { BreadCrumbs } from './components/BreadCrumbs';
import { CategoryCard } from './components/CategoryCard';
import { Filter } from './components/Filter';
import { INITIAL_QUERY_PARAMS } from './components/Filter/constants';
import {
  ACTIVE_FILTER_PREFIX,
  ACTIVE_FILTER_SUFFIX,
  ERROR_BUTTON_RELOAD_LABEL,
  ERROR_HAPPENED_TEXT,
  NO_RESULT_TEXT,
} from './constants';
import { useStyles } from './styles';
import { getActiveFilterQuantity } from './utils';

const List: FC = () => {
  const routeParams = useParams();
  const {
    activeCategory,
    categories,
    categoriesError,
    isCategoriesLoading,
    refetchCategories,
  } = useContext(CategoriesContext);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { queryParams } = useQueryParams(INITIAL_QUERY_PARAMS);

  const classes = useStyles();
  const theme = useTheme();

  const activeFilterQuantity = useMemo(
    () => getActiveFilterQuantity(queryParams),
    [queryParams],
  );

  // Get all level 3 categories (top level)
  const level3Categories = useMemo(() => {
    if (!categories?.length) return [];
    return categories.filter((cat) => cat.level === 3);
  }, [categories]);

  // Get subcategories based on the active category's level
  const subcategories = useMemo(() => {
    if (!categories?.length || !activeCategory) return [];

    // If active category is level 3, show level 4 categories
    if (activeCategory.level === 3) {
      return categories.filter(
        (cat) => cat.level === 4 && cat.parent?.id === activeCategory.id,
      );
    }

    // If active category is level 4, show level 5 categories
    if (activeCategory.level === 4) {
      return categories.filter(
        (cat) => cat.level === 5 && cat.parent?.id === activeCategory.id,
      );
    }

    return [];
  }, [categories, activeCategory]);

  // Determine which categories to display based on the active category
  const currentLevelCategories = useMemo(() => {
    if (activeCategory?.level === 3 || activeCategory?.level === 4) {
      return subcategories;
    }
    return level3Categories;
  }, [activeCategory, level3Categories, subcategories]);

  const filteredCategories = useMemo(
    () =>
      currentLevelCategories.filter((category: Category) => {
        const filterList: boolean[] = [];

        // Favorites filter
        if (queryParams.filterFavorites === 'true') {
          // eslint-disable-next-line fp/no-mutating-methods
          filterList.push(category.isHeadlined);
        }

        // Status filter
        if (Array.isArray(queryParams.filterStatus)) {
          // eslint-disable-next-line fp/no-mutating-methods
          filterList.push(
            queryParams.filterStatus.some(
              (status) => status === category.status,
            ),
          );
        } else {
          // eslint-disable-next-line fp/no-mutating-methods
          filterList.push(queryParams.filterStatus === category.status);
        }

        return filterList.every(Boolean);
      }),
    [currentLevelCategories, queryParams],
  );

  const isAllowedToInteractWithRootCategory = auth.user?.isSuperAdmin();

  const shouldShowAddButton = useMemo(() => {
    if (isCategoriesLoading || !activeCategory) {
      return false;
    }

    if (activeCategory.level === 1 && !isAllowedToInteractWithRootCategory) {
      return false;
    }

    return true;
  }, [
    isCategoriesLoading,
    activeCategory,
    isAllowedToInteractWithRootCategory,
  ]);

  return (
    <div
      className={classes.wrapper}
      data-testid={DataTestIdListPage.LIST_WRAPPER}
    >
      <div className={classes.wrapperTopList}>
        <BreadCrumbs />
        <Filter isFilterOpen={isFilterOpen} setIsFilterOpen={setIsFilterOpen} />
      </div>

      <div className={classes.innerWrapper}>
        {/* Loading placeholders */}
        {isCategoriesLoading && (
          <Skeletons height={LARGE_CARD_ITEM_HEIGHT} quantity={4} spacing={2} />
        )}

        {/* API Error */}
        {categoriesError && (
          <div className={classes.wrapperCenter}>
            <Error color="primary" fontSize="large" />
            <Typography color="primary" variant="h5">
              {categoriesError?.message || ERROR_HAPPENED_TEXT}
            </Typography>
            <br />
            <Button
              onClick={() => {
                refetchCategories();
              }}
              variant="outlined"
            >
              {ERROR_BUTTON_RELOAD_LABEL}
            </Button>
          </div>
        )}

        {/* Empty API result */}
        {!isCategoriesLoading &&
          !categoriesError &&
          !filteredCategories.length && (
            <div className={classes.wrapperCenter}>
              <Typography variant="h5" color="primary">
                {NO_RESULT_TEXT}
              </Typography>

              {!!activeFilterQuantity && (
                <Chip
                  color="primary"
                  className={classes.activeFilterBadge}
                  data-testid={DataTestIdListPage.FILTER_BUTTON}
                  icon={<FilterList />}
                  label={`${activeFilterQuantity} ${pluralize(
                    ACTIVE_FILTER_PREFIX,
                    activeFilterQuantity,
                  )} ${pluralize(ACTIVE_FILTER_SUFFIX, activeFilterQuantity)}`}
                  onClick={() => {
                    setIsFilterOpen(true);
                  }}
                />
              )}
            </div>
          )}

        {/* List cards */}
        <FlipMove>
          <Stack spacing={2}>
            {filteredCategories.map((category: Category) => (
              <CategoryCard category={category} key={category.id} />
            ))}
          </Stack>
        </FlipMove>
      </div>

      {shouldShowAddButton && (
        <Tooltip
          sx={{
            position: 'fixed',
            bottom: theme.spacing(3),
            right: theme.spacing(4),
            zIndex: 2,
          }}
          title="Ajouter un élément"
        >
          <Fab
            color="primary"
            onClick={() => {
              const search = new URLSearchParams({
                from: 'Breadcrumbs',
                parentId: activeCategory?.id || '',
              }).toString();
              navigateTo({
                pathname: paths.PAGE_CREATE,
                routeParams,
                opts: { search },
              });
            }}
          >
            <Add />
          </Fab>
        </Tooltip>
      )}
    </div>
  );
};

export { List };
